import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { formatDate } from "lopermut"
import { SquareText, UserRound } from "lucide-react"
import { CompanyClientCell } from "modules/customers/components/CompanyClientCell"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CustomerCell } from "modules/shared/components/CustomerCell"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { IssueOccurrenceCountBadge } from "modules/support/components/entities/IssueOccurrenceCountBadge"
import { IssueCloseButton } from "modules/support/components/entities/IssueCloseButton"
import { IssueStatusBadge } from "modules/support/components/entities/IssueUrgencyIdentifier"
import { useSupportIssuesQuery } from "modules/support/hooks/useSupportIssuesQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/support/list/route"
import { SupportIssuesResponseDataSchema, SupportSortFields } from "schema"
import { z } from "zod"
import { TicketIntegrationIcon } from "../TicketIntegrationBadges"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof SupportIssuesResponseDataSchema>[number]
>()

type IssuesQueryReturn = ReturnType<typeof useSupportIssuesQuery>

type Props = {
  issues: IssuesQueryReturn["data"]
  isPending: IssuesQueryReturn["isPending"]
  fields: typeof SupportSortFields
  onSort: (field: (typeof SupportSortFields)[number]) => void
  search: z.infer<typeof SearchSchema>
}

export function IssuesTable({ issues, isPending, fields, onSort, search }: Props) {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()

  const someIssueHasIntegration = useMemo(
    () => Boolean(issues && issues.some((issue) => issue.integrations.length > 0)),
    [issues],
  )

  const columns = useMemo(
    () =>
      columnHelper.columns(
        [
          isAmphoraCompany &&
            columnHelper.display({
              id: "orderId",
              header: t("support.issues.table.orderId"),
              cell: ({ row }) => row.original.conversation?.orderId ?? "-",
            }),
          columnHelper.display({
            id: "indicators",
            header: () => null,
            cell: ({ row }) => {
              const issue = row.original
              const isReopened = issue.status === "Reopened"
              const previousStatus = issue.reopeningUpdate?.previousStatus
              const reopenedTooltip = previousStatus
                ? t("support.entities.reopenedTooltip", {
                    previousStatus: t(`support.entities.issueStatus.${previousStatus}`),
                  })
                : t("support.entities.issueStatus.Reopened")

              return (
                <span className="flex flex-row items-center gap-1.5">
                  <span className="flex size-3 items-center justify-center">
                    {(isReopened || issue.hasUnansweredCustomerReply) && (
                      <Tooltip>
                        <TooltipTrigger>
                          <Pulse
                            className={
                              isReopened ? "fill-testing-1" : "fill-unanswered-customer-reply"
                            }
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          {isReopened
                            ? reopenedTooltip
                            : t("support.tickets.unansweredCustomerReply")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                  <span className="flex size-4 items-center justify-center">
                    {issue.hasHumanReply && (
                      <Tooltip>
                        <TooltipTrigger>
                          <UserRound
                            className={
                              isReopened
                                ? "size-4 text-testing-1"
                                : "size-4 text-support dark:text-support-secondary"
                            }
                          />
                        </TooltipTrigger>
                        <TooltipContent>{t("support.tickets.humanReplyTooltip")}</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                </span>
              )
            },
            meta: {
              className: "w-px whitespace-nowrap px-2",
            },
          }),
          columnHelper.accessor("urgency", {
            header: t("support.issues.table.urgency"),
            cell: ({ row }) => <IssueStatusBadge issue={row.original} />,
            meta: {
              colSpan: 1,
            },
          }),
          columnHelper.accessor("subject", {
            header: t("support.issues.table.subject"),
            cell: (props) => (
              <span className="flex items-center justify-between gap-1.5">
                <Tooltip>
                  <TooltipTrigger className="block min-w-0 truncate">
                    {props.getValue()}
                  </TooltipTrigger>
                  <TooltipContent className="max-w-80">{props.getValue()}</TooltipContent>
                </Tooltip>
                {props.row.original.summary && (
                  <Tooltip>
                    <TooltipTrigger className="shrink-0 cursor-pointer">
                      <SquareText className="size-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80">
                      {props.row.original.summary}
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
            ),
            meta: {
              colSpan: 6,
            },
          }),
          !isAmphoraCompany &&
            columnHelper.accessor("customer", {
              header: () => t("support.issues.table.customer"),
              cell: ({ row }) => <CustomerCell customer={row.original.customer} />,
            }),
          isAmphoraCompany &&
            columnHelper.display({
              id: "client",
              header: t("support.issues.table.client"),
              cell: ({ row }) => <CompanyClientCell client={row.original.customer.client} />,
            }),
          columnHelper.accessor("campaign", {
            header: t("support.issues.table.campaign"),
            cell: (props) => props.getValue()?.name ?? "-",
            meta: {
              colSpan: 2,
            },
          }),
          columnHelper.accessor("occurrenceCount", {
            header: t("support.issues.table.occurrences"),
            cell: (props) => <IssueOccurrenceCountBadge count={props.getValue()} />,
            meta: {
              colSpan: 1,
              align: "center",
            },
          }),
          columnHelper.accessor("createdAt", {
            header: t("support.issues.table.createdAt"),
            cell: (props) => <DateDisplay date={props.getValue()} />,
            meta: {
              colSpan: 1,
              align: "right",
            },
          }),
          someIssueHasIntegration &&
            columnHelper.display({
              id: "integrations",
              header: () => null,
              cell: ({ row }) => (
                <span className="flex flex-row gap-2">
                  {row.original.integrations.map((integration) => (
                    <Tooltip key={`${integration.kind}-${integration.externalId}`}>
                      <TooltipTrigger>
                        <TicketIntegrationIcon kind={integration.kind} className="size-4" />
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("support.entities.escalatedThrough", {
                          kind: integration.kind,
                          createdAt: formatDate(integration.createdAt, "Pp"),
                        })}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </span>
              ),
              meta: {
                className: "w-px whitespace-nowrap px-3",
              },
            }),
          columnHelper.display({
            id: "resolve",
            header: () => null,
            cell: ({ row }) => <IssueCloseButton issue={row.original} />,
            meta: {
              className: "w-px whitespace-nowrap py-0 pl-0 pr-2",
            },
          }),
        ].filter(Boolean),
      ),
    [isAmphoraCompany, someIssueHasIntegration, t],
  )

  const table = useTable({
    data: issues ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || issues === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (issues.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("support.issues.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/support/list/$customerId",
          params: { customerId: row.original.customer.id },
          search: {
            ...search,
            conversationId: row.original.conversationId
              ? Number(row.original.conversationId)
              : undefined,
            tab: "issues",
            issueId: row.original.id,
            supportTab: "issues",
          },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof SupportIssuesResponseDataSchema>[number], unknown>,
  ) {
    if (!fields.includes(header.id as (typeof SupportSortFields)[number])) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as (typeof SupportSortFields)[number]),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
